class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.moveCssLinks();
  }

  reload() {
    this.historyRefreshController?.abort();
    const controller = new AbortController();
    this.historyRefreshController = controller;
    // Do not let an older refresh overwrite a newer cart action.
    const cancel = () => controller.abort();
    const events = ['cart-add', 'cart-remove', 'cart-update', 'submit', 'change'];
    events.forEach(name => document.addEventListener(name, cancel));
    window.addEventListener('pagehide', cancel);
    const sections = this.getSectionsToRender().map((section) => section.id);
    const url = new URL(window.routes?.cart_url || '/cart', window.location.origin);
    url.searchParams.set('sections', sections.join(','));
    return fetch(url.toString(), { cache: 'no-store', signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Could not refresh the cart');
        return response.json();
      })
      .then((response) => {
        if (controller.signal.aborted) return;
        // Shopify may return null for a section. Keep the current UI intact.
        const drawer = new DOMParser().parseFromString(response['cart-drawer'] || '', 'text/html');
        const wrapper = drawer.querySelector('cart-drawer');
        const icon = new DOMParser().parseFromString(response['cart-icon-bubble'] || '', 'text/html');
        if (!wrapper || !drawer.querySelector('#CartDrawer') || !icon.querySelector('.shopify-section')) {
          throw new Error('Incomplete cart sections');
        }
        this.renderContents({ sections: response }, true);
        this.classList.toggle('is-empty', wrapper.classList.contains('is-empty'));
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      })
      .finally(() => {
        events.forEach(name => document.removeEventListener(name, cancel));
        window.removeEventListener('pagehide', cancel);
      });
  }

  moveCssLinks() {
    const cssLinks = this.querySelectorAll('link[rel="stylesheet"][href*=".css"]');
    cssLinks.forEach(link => {
      // Check if the link already exists in the document head
      const href = link.href;
      if (!document.head.querySelector(`link[href="${href}"]`)) {
        // Clone the link element
        const newLink = link.cloneNode(true);
        // Append the cloned link to the document head
        document.head.appendChild(newLink);
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.removeDeleteTabIndex();

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);

        publish(PUB_SUB_EVENTS.cartOpen);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    publish(PUB_SUB_EVENTS.cartClose);
  }

  /**
   * Ensure all remove buttons can be tabbed to
   */
  removeDeleteTabIndex() {
    this.querySelectorAll('.cart-remove-button').forEach((button) => {
      button.removeAttribute('tabindex')
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, preventOpen = false) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    });

    this.removeDeleteTabIndex();

    if (theme.settings.cartDrawer.atcAction === 'drawer' && !preventOpen) {
      setTimeout(() => {
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// A page restored from browser history can contain an outdated cart drawer.
// Refresh server-rendered cart sections without reloading or scrolling the page.
window.addEventListener('pageshow', (event) => {
  const navigation = performance.getEntriesByType('navigation')[0];
  if (event.persisted || navigation?.type === 'back_forward') {
    document.querySelector('cart-drawer')?.reload();
  }
});
